import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
const coarsePointerQuery = window.matchMedia('(pointer: coarse)')
const tabletQuery = window.matchMedia('(max-width: 1023px)')
const mobileQuery = window.matchMedia('(max-width: 767px)')

let lenis
let cursorFrame = 0
let resizeTimer
const cleanup = []

const prefersReducedMotion = () => false
const canUseFullMotion = () => true
const canUseDesktopEffects = () => !coarsePointerQuery.matches && !tabletQuery.matches && canUseFullMotion()
const isMobile = () => mobileQuery.matches || navigator.hardwareConcurrency <= 4 || 'ontouchstart' in window
const isLowEnd = () => navigator.hardwareConcurrency <= 4
const svgNS = 'http://www.w3.org/2000/svg'

function addCleanup(fn) {
  cleanup.push(fn)
}

function markWillAnimate(targets) {
  gsap.utils.toArray(targets).forEach((target) => target?.classList?.add('will-animate'))
}

function clearWillAnimate(targets) {
  gsap.utils.toArray(targets).forEach((target) => target?.classList?.remove('will-animate'))
}

function svgEl(name, attrs = {}) {
  const el = document.createElementNS(svgNS, name)
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value))
  return el
}

function generateSvgPanels() {
  const fiber = document.querySelector('[data-svg-generate="fiber-knot"]')
  if (fiber && !fiber.children.length) {
    for (let i = 0; i < 25; i += 1) {
      const offset = i * 10
      const path = svgEl('path', {
        class: 'fiber-segment',
        d: `M${470 + Math.sin(i) * 24},${435 + Math.cos(i) * 18} C${560 + offset * 0.7},${250 + (i % 5) * 24} ${860 - offset * 0.45},${650 - (i % 6) * 28} ${970 + Math.cos(i) * 28},${455 + Math.sin(i) * 22}`,
        fill: 'none',
        stroke: 'url(#hero-grad)',
        'stroke-width': String(10 - (i % 5)),
        'stroke-linecap': 'round',
        opacity: String(0.42 + (i % 7) * 0.055),
        'stroke-dasharray': '90 520',
      })
      fiber.appendChild(path)
    }
  }

  const stripeTargets = [
    document.querySelector('[data-svg-generate="spectrum-stripes"]'),
    document.querySelector('[data-svg-generate="spectrum-ghost"]'),
  ]
  stripeTargets.forEach((target) => {
    if (!target || target.children.length) return
    for (let i = 0; i < 16; i += 1) {
      target.appendChild(svgEl('rect', {
        x: String(-260 + i * 116),
        y: '-420',
        width: String(i % 3 === 0 ? 92 : i % 3 === 1 ? 58 : 128),
        height: '1780',
        fill: 'url(#spectrum-grad)',
        opacity: target.classList.contains('spectrum-ghost') ? '0.06' : String(0.1 + (i % 5) * 0.035),
      }))
    }
  })

  const glitch = document.querySelector('[data-svg-generate="glitch-bars"]')
  if (glitch && !glitch.children.length) {
    for (let i = 0; i < 28; i += 1) {
      const grad = svgEl('linearGradient', { id: `glitch-grad-${i}`, x1: '0%', y1: '0%', x2: '0%', y2: '100%' })
      ;['#ff3366', '#ff9900', '#00ccff', '#aa33ff'].forEach((color, j) => {
        grad.appendChild(svgEl('stop', { offset: `${j * 33}%`, 'stop-color': color }))
      })
      glitch.closest('svg')?.querySelector('defs')?.appendChild(grad)
      glitch.appendChild(svgEl('rect', {
        class: 'glitch-bar',
        x: String(584 + i * 10),
        y: String(150 + (i % 6) * 22),
        width: String(3 + (i % 4)),
        height: String(600 - (i % 7) * 34),
        fill: `url(#glitch-grad-${i})`,
        rx: '2',
      }))
    }
  }

  const mini = document.querySelector('[data-svg-generate="mini-bars"]')
  if (mini && !mini.children.length) {
    for (let i = 0; i < 10; i += 1) {
      mini.appendChild(svgEl('rect', {
        class: 'mini-bar',
        x: String(650 + i * 16),
        y: String(690 - (i % 4) * 10),
        width: '8',
        height: String(36 + (i % 4) * 10),
        fill: 'url(#glitch-grad)',
        rx: '4',
      }))
    }
  }

  const stars = document.querySelector('[data-svg-generate="stars"]')
  if (stars && !stars.children.length) {
    const count = isLowEnd() ? 30 : 60
    for (let i = 0; i < count; i += 1) {
      stars.appendChild(svgEl('circle', {
        class: `space-star star-layer-${i % 3}`,
        cx: String((i * 137) % 1440),
        cy: String(80 + ((i * 89) % 540)),
        r: String(0.8 + (i % 3) * 0.45),
        fill: '#fff',
        opacity: String(0.35 + (i % 5) * 0.11),
      }))
    }
  }
}

function initLenis() {
  if (prefersReducedMotion()) return

  lenis = new Lenis({
    lerp: 0.09,
    duration: 1.15,
    smoothWheel: true,
    syncTouch: false,
  })

  const updateScrollTrigger = () => ScrollTrigger.update()
  const tick = (time) => lenis.raf(time * 1000)

  lenis.on('scroll', updateScrollTrigger)
  gsap.ticker.add(tick)
  gsap.ticker.lagSmoothing(0)

  addCleanup(() => {
    gsap.ticker.remove(tick)
    lenis?.off?.('scroll', updateScrollTrigger)
    lenis?.destroy()
    lenis = undefined
  })
}

function initCursor() {
  if (!canUseDesktopEffects()) return

  const cursor = document.createElement('div')
  cursor.className = 'custom-cursor'
  document.body.appendChild(cursor)

  let mouseX = window.innerWidth / 2
  let mouseY = window.innerHeight / 2
  let cursorX = mouseX
  let cursorY = mouseY

  const onMove = (event) => {
    mouseX = event.clientX
    mouseY = event.clientY
  }

  const onOver = (event) => {
    if (event.target.closest('a, button, [data-cursor-hover]')) {
      cursor.classList.add('cursor-hover')
    }
  }

  const onOut = (event) => {
    if (event.target.closest('a, button, [data-cursor-hover]')) {
      cursor.classList.remove('cursor-hover')
    }
  }

  function animateCursor() {
    cursorX += (mouseX - cursorX) * 0.16
    cursorY += (mouseY - cursorY) * 0.16
    cursor.style.setProperty('--cursor-x', `${cursorX}px`)
    cursor.style.setProperty('--cursor-y', `${cursorY}px`)
    cursorFrame = requestAnimationFrame(animateCursor)
  }

  document.addEventListener('mousemove', onMove, { passive: true })
  document.addEventListener('mouseover', onOver)
  document.addEventListener('mouseout', onOut)
  animateCursor()

  addCleanup(() => {
    cancelAnimationFrame(cursorFrame)
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseover', onOver)
    document.removeEventListener('mouseout', onOut)
    cursor.remove()
  })
}

function initNavigation() {
  const nav = document.querySelector('nav')
  const navToggle = document.querySelector('.nav-toggle')
  if (!nav) return

  const closeMenu = () => {
    nav.classList.remove('nav-open')
    navToggle?.setAttribute('aria-expanded', 'false')
    navToggle?.setAttribute('aria-label', 'Open menu')
  }

  ScrollTrigger.create({
    trigger: '#hero',
    start: 'bottom top+=80',
    onEnter: () => nav.classList.add('nav-scrolled'),
    onLeaveBack: () => nav.classList.remove('nav-scrolled'),
  })

  const toggleMenu = () => {
    const isOpen = nav.classList.toggle('nav-open')
    navToggle?.setAttribute('aria-expanded', String(isOpen))
    navToggle?.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu')
  }

  const onToggleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    toggleMenu()
  }

  navToggle?.addEventListener('click', toggleMenu)
  navToggle?.addEventListener('keydown', onToggleKeyDown)

  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    const onClick = (event) => {
      const hash = anchor.getAttribute('href')
      const target = hash ? document.querySelector(hash) : null
      if (!target) return

      event.preventDefault()
      closeMenu()
      if (lenis && !prefersReducedMotion()) {
        lenis.scrollTo(target, { offset: 0, duration: 1.05 })
      } else {
        target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
      }
    }

    anchor.addEventListener('click', onClick)
    addCleanup(() => anchor.removeEventListener('click', onClick))
  })

  const onResize = () => {
    if (!mobileQuery.matches) closeMenu()
  }

  window.addEventListener('resize', onResize, { passive: true })
  addCleanup(() => {
    navToggle?.removeEventListener('click', toggleMenu)
    navToggle?.removeEventListener('keydown', onToggleKeyDown)
    window.removeEventListener('resize', onResize)
  })
}

function initHero() {
  const knot = document.querySelector('.fiber-knot')
  const fibers = gsap.utils.toArray('.fiber-segment')
  const heroText = document.querySelector('.hero-display')
  const heroCaption = document.querySelector('.hero-caption')
  const heroButton = document.querySelector('#hero .pill-btn')
  const orbits = gsap.utils.toArray('.hero-orbit')

  markWillAnimate([heroText, heroCaption, heroButton])

  if (!canUseFullMotion()) {
    gsap.set([heroText, heroCaption, heroButton], { clearProps: 'all', opacity: 1 })
    clearWillAnimate([heroText, heroCaption, heroButton])
    return
  }

  gsap.timeline({
    defaults: { ease: 'power3.out' },
    onComplete: () => clearWillAnimate([heroText, heroCaption, heroButton]),
  })
    .from(heroText, { y: 54, opacity: 0, duration: 1.25 })
    .from(heroCaption, { y: 22, opacity: 0, duration: 0.95 }, '-=0.55')
    .from(heroButton, { y: 16, opacity: 0, duration: 0.8 }, '-=0.45')

  if (knot) {
    gsap.set(knot, { transformOrigin: '50% 50%', scale: 0.86 })
    gsap.to(knot, { rotate: 360, duration: 30, repeat: -1, ease: 'none' })
    gsap.to(fibers, {
      strokeDashoffset: -620,
      duration: 2.4,
      repeat: -1,
      ease: 'none',
      stagger: 0.04,
    })

    gsap.to(knot, {
      scale: 1,
      transformOrigin: '50% 50%',
      ease: 'none',
      scrollTrigger: {
        trigger: '#hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 0.8,
      },
    })
  }

  orbits.forEach((circle, index) => {
    gsap.set(circle, { transformOrigin: '50% 50%' })
    gsap.to(circle, {
      rotate: index % 2 ? -360 : 360,
      scale: 1 + index * 0.035,
      duration: 24 + index * 8,
      repeat: -1,
      ease: 'none',
    })
  })

  gsap.to([heroText, heroCaption, heroButton], {
    opacity: 0,
    y: -32,
    filter: 'blur(14px)',
    ease: 'none',
    scrollTrigger: {
      trigger: '#hero',
      start: 'top top',
      end: '65% top',
      scrub: true,
    },
  })
}

function initSpectrum() {
  const headline = document.querySelector('.spectrum-headline')
  const indices = gsap.utils.toArray('.spectrum-index')
  const stripeGroup = document.querySelector('#research .spectrum-stripes:not(.spectrum-ghost)')
  const ghostGroup = document.querySelector('#research .spectrum-ghost')

  if (!canUseFullMotion()) {
    gsap.set(headline, { clipPath: 'inset(0 0 0 0)' })
    indices.forEach((el) => { el.textContent = el.dataset.target || '00' })
    return
  }

  markWillAnimate([headline, stripeGroup, ghostGroup, ...indices])

  gsap.fromTo(headline,
    { clipPath: 'inset(0 100% 0 0)', y: 24, opacity: 0.72 },
    {
      clipPath: 'inset(0 0% 0 0)',
      y: 0,
      opacity: 1,
      duration: 1.25,
      ease: 'power3.out',
      onComplete: () => clearWillAnimate(headline),
      scrollTrigger: { trigger: '#research', start: 'top 72%' },
    })

  indices.forEach((el, index) => {
    const target = Number.parseInt(el.dataset.target || '0', 10)
    gsap.to(el, {
      textContent: target,
      duration: 1.8,
      ease: 'power3.out',
      snap: { textContent: 1 },
      delay: index * 0.055,
      onUpdate: () => {
        el.textContent = String(Math.round(Number(el.textContent))).padStart(2, '0')
      },
      onComplete: () => clearWillAnimate(el),
      scrollTrigger: { trigger: '#research', start: 'top 68%' },
    })
  })

  gsap.to(stripeGroup, {
    x: 90,
    opacity: 0.92,
    ease: 'none',
    onComplete: () => clearWillAnimate(stripeGroup),
    scrollTrigger: {
      trigger: '#research',
      start: 'top bottom',
      end: 'bottom top',
      scrub: 1.2,
    },
  })

  gsap.to(ghostGroup, {
    x: 150,
    ease: 'none',
    onComplete: () => clearWillAnimate(ghostGroup),
    scrollTrigger: {
      trigger: '#research',
      start: 'top bottom',
      end: 'bottom top',
      scrub: 0.8,
    },
  })
}

function initMaterial() {
  const pills = gsap.utils.toArray('.chrome-pill')
  const reflections = gsap.utils.toArray('#material svg > g[opacity]')
  const gradients = gsap.utils.toArray('#material linearGradient[id^="chrome-"]')
  if (isLowEnd()) {
    document.querySelector('.secondary-pill-light')?.remove()
  }

  if (!canUseFullMotion()) {
    gsap.set(pills, { opacity: 1, y: 0 })
    return
  }

  markWillAnimate([...pills, ...reflections])

  gsap.from(pills, {
    y: 96,
    opacity: 0,
    duration: 1.1,
    stagger: 0.085,
    ease: 'power3.out',
    onComplete: () => clearWillAnimate(pills),
    scrollTrigger: { trigger: '#material', start: 'top 70%' },
  })

  gsap.from(reflections, {
    opacity: 0,
    y: 34,
    duration: 1.4,
    ease: 'power3.out',
    onComplete: () => clearWillAnimate(reflections),
    scrollTrigger: { trigger: '#material', start: 'top 68%' },
  })

  pills.forEach((pill, index) => {
    gsap.to(pill, {
      y: index % 2 ? -3 : 3,
      duration: 4,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
      delay: index * 0.16,
    })

    const onEnter = () => {
      const gradient = gradients[index]
      gsap.to(pill, { x: index % 2 ? -10 : 10, duration: 0.45, ease: 'power3.out' })
      if (gradient) {
        gsap.to(gradient, { attr: { x1: '100%', x2: '0%' }, duration: 0.65, ease: 'sine.inOut' })
      }
    }
    const onLeave = () => {
      const gradient = gradients[index]
      gsap.to(pill, { x: 0, duration: 0.5, ease: 'power3.out' })
      if (gradient) {
        gsap.to(gradient, { attr: { x1: '0%', x2: '0%' }, duration: 0.65, ease: 'sine.inOut' })
      }
    }

    pill.addEventListener('mouseenter', onEnter)
    pill.addEventListener('mouseleave', onLeave)
    addCleanup(() => {
      pill.removeEventListener('mouseenter', onEnter)
      pill.removeEventListener('mouseleave', onLeave)
    })
  })
}

function initAura() {
  const phone = document.querySelector('.aura-phone')
  const glow = document.querySelector('.aura-glow')
  const waves = gsap.utils.toArray('.phone-wave')

  if (!canUseFullMotion()) return

  markWillAnimate([phone, glow, ...waves])
  gsap.set(waves, { strokeDasharray: 180, strokeDashoffset: 180 })
  gsap.fromTo(phone,
    { y: 90, rotateY: -15, opacity: 0.2, transformPerspective: 900, transformOrigin: '50% 50%' },
    {
      y: 0,
      rotateY: 0,
      opacity: 1,
      duration: 1.2,
      ease: 'power3.out',
      onComplete: () => clearWillAnimate(phone),
      scrollTrigger: { trigger: '#aura', start: 'top 72%' },
    })
  gsap.to(glow, { scale: 1.03, transformOrigin: '50% 50%', duration: 6, repeat: -1, yoyo: true, ease: 'sine.inOut' })
  gsap.to(waves, {
    strokeDashoffset: -180,
    duration: 3.4,
    repeat: -1,
    ease: 'none',
    stagger: 0.2,
    onComplete: () => clearWillAnimate(waves),
  })
}

/**
 * Creates a deterministic character scramble that resolves through GSAP progress.
 * It avoids per-frame DOM measurement and only mutates textContent.
 */
function scrambleElement(element, duration = 0.9) {
  const finalText = element.dataset.scramble || element.textContent || ''
  const glyphs = '01/\\|#$%+*CHROMA'
  const state = { progress: 0 }

  return gsap.fromTo(state, { progress: 0 }, {
    progress: 1,
    duration,
    ease: 'power3.out',
    onUpdate: () => {
      const solved = Math.floor(finalText.length * state.progress)
      element.textContent = finalText.split('').map((char, index) => {
        if (char === ' ' || index < solved) return char
        return glyphs[(index + Math.floor(state.progress * 24)) % glyphs.length]
      }).join('')
    },
    onComplete: () => {
      element.textContent = finalText
    },
  })
}

function initSignal() {
  const allBars = gsap.utils.toArray('.glitch-bar')
  const bars = isMobile() ? allBars.filter((_, index) => index % 2 === 0).slice(0, 20) : allBars
  const scrambleTargets = gsap.utils.toArray('.scramble-text, .signal-captions [data-scramble]')

  gsap.set(allBars, { transformOrigin: '50% 100%' })

  if (!canUseFullMotion()) {
    allBars.forEach((bar) => gsap.set(bar, { scaleY: 1, scaleX: 1 }))
    return
  }

  markWillAnimate([...bars, ...scrambleTargets])

  gsap.fromTo(bars,
    { scaleY: 0, opacity: 0.2 },
    {
      scaleY: 1,
      opacity: 1,
      duration: 0.72,
      ease: 'power3.out',
      stagger: { each: 0.018, from: 'center' },
      onComplete: () => clearWillAnimate(bars),
      scrollTrigger: { trigger: '#signal', start: 'top 68%' },
    })

  ScrollTrigger.create({
    trigger: '#signal',
    start: 'top 70%',
    once: true,
    onEnter: () => {
      scrambleTargets.forEach((target, index) => {
        gsap.delayedCall(index * 0.075, () => scrambleElement(target, index === 0 ? 1.1 : 0.72))
      })
    },
  })

  const glitchLoop = () => {
    const subset = gsap.utils.shuffle(bars.slice()).slice(0, Math.max(5, Math.floor(bars.length * 0.25)))
    gsap.timeline({ delay: gsap.utils.random(0.9, 2.4) })
      .to(subset, {
        scaleX: 0.08,
        opacity: () => gsap.utils.random(0.45, 0.9),
        duration: 0.055,
        ease: 'power3.in',
        stagger: { amount: 0.08, from: 'random' },
      })
      .to(subset, {
        scaleX: 1,
        opacity: 1,
        duration: 0.13,
        ease: 'power3.out',
        stagger: { amount: 0.06, from: 'random' },
        onComplete: glitchLoop,
      })
  }

  ScrollTrigger.create({
    trigger: '#signal',
    start: 'top bottom',
    end: 'bottom top',
    onEnter: glitchLoop,
    once: true,
  })
}

function initCapability() {
  const cards = gsap.utils.toArray('.capability-card')
  if (!canUseFullMotion()) return

  markWillAnimate(cards)
  gsap.fromTo(cards,
    { scale: 0.86, opacity: 0, y: 26 },
    {
      scale: 1,
      opacity: 1,
      y: 0,
      duration: 0.72,
      ease: 'power3.out',
      stagger: { each: 0.07, grid: [3, 3], from: 'start' },
      onComplete: () => clearWillAnimate(cards),
      scrollTrigger: { trigger: '#capability', start: 'top 72%' },
    })
}

function initSpace() {
  const layers = [
    { selector: '#depth-back', speed: -30, duration: 6 },
    { selector: '#depth-mid', speed: -80, duration: 6 },
    { selector: '#depth-front', speed: -180, duration: 6 },
    { selector: '#depth-accent', speed: -240, duration: 6 },
    { selector: '#depth-glow-line', speed: -300, duration: 6 },
  ]
  const stars = gsap.utils.toArray('.space-star')

  if (!canUseFullMotion()) return

  layers.forEach((layer) => {
    const el = document.querySelector(layer.selector)
    const altD = el?.dataset.altD
    if (!el) return

    markWillAnimate(el)

    if (!isMobile()) {
      gsap.to(el, {
        y: tabletQuery.matches ? layer.speed * 0.45 : layer.speed,
        ease: 'none',
        scrollTrigger: {
          trigger: '#space',
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      })
    }

    if (altD) {
      gsap.to(el, {
        attr: { d: altD },
        duration: layer.duration,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      })
    }
  })

  stars.forEach((star, index) => {
    gsap.to(star, {
      y: [-45, -95, -140][index % 3],
      ease: 'none',
      scrollTrigger: {
        trigger: '#space',
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
      },
    })
  })
}

function initSurface() {
  const posters = gsap.utils.toArray('.poster-svg')
  const turbulence = gsap.utils.toArray('#surface feTurbulence')

  if (!canUseFullMotion()) {
    gsap.set(posters, { scale: 1 })
    return
  }

  markWillAnimate(posters)

  gsap.fromTo(posters,
    { scale: 0.92, y: 42, opacity: 0.72, transformOrigin: '50% 50%' },
    {
      scale: 1,
      y: 0,
      opacity: 1,
      duration: 1.2,
      stagger: 0.12,
      ease: 'power3.out',
      onComplete: () => clearWillAnimate(posters),
      scrollTrigger: { trigger: '#surface', start: 'top 75%' },
    })

  if (!isMobile()) {
    gsap.to(turbulence, {
      attr: { baseFrequency: '0.86' },
      ease: 'none',
      scrollTrigger: {
        trigger: '#surface',
        start: 'top bottom',
        end: 'center center',
        scrub: true,
      },
    })
  }
}

function initFooter() {
  const ribbons = gsap.utils.toArray('.ribbon-path')
  if (!canUseFullMotion() || isLowEnd()) return
  ribbons.forEach((path, index) => {
    const altD = path.dataset.altD
    if (!altD) return
    gsap.to(path, {
      attr: { d: altD },
      duration: 3 + index * 0.15,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    })
  })
}

function initGlobalGrain() {
  if (prefersReducedMotion() || isMobile()) {
    document.querySelector('.grain-overlay')?.classList.add('is-static')
  }
}

function initResizeRefresh() {
  const onResize = () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => ScrollTrigger.refresh(), 250)
  }

  window.addEventListener('resize', onResize, { passive: true })
  addCleanup(() => {
    clearTimeout(resizeTimer)
    window.removeEventListener('resize', onResize)
  })
}

export function initApp() {
  generateSvgPanels()
  initLenis()
  initGlobalGrain()
  initCursor()
  initNavigation()
  initHero()
  initSpectrum()
  initMaterial()
  initAura()
  initSignal()
  initCapability()
  initSpace()
  initSurface()
  initFooter()
  initResizeRefresh()

  ScrollTrigger.refresh()
}

export function destroyApp() {
  cleanup.splice(0).forEach((fn) => fn())
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
  gsap.globalTimeline.clear()
}
